import { useTranslation } from 'react-i18next';
import { formatTimezoneLabel } from '@/lib/format/timezone';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { buildHeatmapGrid } from '@/lib/social-analytics/buildHeatmapGrid';
import { formatHourLabel, formatHourRange } from '@/lib/social-analytics/formatHourLabel';
import { formatMetric } from '@/lib/social-analytics/formatMetric';
import { resolveInsightCardState } from '@/lib/social-analytics/resolveInsightCardState';
import { cn } from '@/lib/utils';
import type { InsightCardProps } from './FormatInsightsCard';
import { InsightCardBody } from './InsightCardBody';

export const PostingTimeHeatmap = ({ insights, range, error, errorKey, onRetry, timezone }: InsightCardProps & { timezone: string }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language.startsWith('pt') ? 'pt-BR' : 'en-US';
  const state = resolveInsightCardState({ range, eligiblePosts: insights?.eligible_posts ?? null, error });
  const grid = buildHeatmapGrid(insights?.hourly_slots ?? []);
  const weekdayName = (weekday: number) => t(`analytics.insights.times.weekdaysLong.${weekday}`);
  return (
    <Card className="border-border/70 shadow-none">
      <CardHeader className="border-b border-border/60 pb-4">
        <CardTitle className="text-base">{t('analytics.insights.times.title')}</CardTitle>
        {(state === 'ready' || state === 'notEnough') && <p className="text-xs text-muted-foreground">{t('analytics.insights.subtitle', { count: insights?.eligible_posts ?? 0 })}</p>}
      </CardHeader>
      <CardContent className="p-4 sm:p-5">
        <InsightCardBody state={state} minPostAgeDays={insights?.min_post_age_days} errorKey={errorKey} onRetry={onRetry}>
          <div role="table" aria-label={t('analytics.insights.times.title')} className="grid grid-cols-[3rem_repeat(7,minmax(0,1fr))] gap-x-1 gap-y-0.5 text-[11px]">
            <div role="row" className="contents">
              <span role="columnheader" aria-hidden="true" />
              {grid.weekdays.map((weekday) => (
                <span key={weekday} role="columnheader" className="pb-1 text-center text-muted-foreground">{t(`analytics.insights.times.weekdays.${weekday}`)}</span>
              ))}
            </div>
            {grid.rows.map((row) => (
              <div key={row.hour} role="row" className="contents">
                {/* Labels on every other hour keep the 24 rows legible, as in the reference layout. */}
                <span role="rowheader" className="self-center whitespace-nowrap pr-1 text-right leading-none text-muted-foreground">
                  {row.hour % 2 === 1 ? formatHourLabel(row.hour, locale) : ''}
                </span>
                {row.cells.map((cell) => {
                  const slot = `${weekdayName(cell.weekday)} ${formatHourRange(cell.hour)}`;
                  const hasPosts = cell.medianViews !== null;
                  const label = hasPosts
                    ? t('analytics.insights.times.cell', { slot, median: formatMetric(cell.medianViews, locale), count: cell.posts })
                    : t('analytics.insights.times.cellEmpty', { slot });
                  return (
                    <div
                      key={cell.weekday}
                      role="cell"
                      title={label}
                      aria-label={label}
                      data-testid={`heatmap-cell-${cell.weekday}-${cell.hour}`}
                      data-has-posts={hasPosts}
                      className={cn('h-3 rounded-sm', !hasPosts && 'bg-muted/40', cell === grid.best && 'ring-2 ring-emerald-500 ring-offset-1 ring-offset-background')}
                      style={hasPosts ? { backgroundColor: 'hsl(var(--primary))', opacity: 0.15 + 0.85 * cell.intensity } : undefined}
                    />
                  );
                })}
              </div>
            ))}
          </div>
          {grid.best && (
            <p className="mt-3 text-xs">
              <span className="font-semibold">{t('analytics.insights.times.best', { slot: `${weekdayName(grid.best.weekday)} ${formatHourLabel(grid.best.hour, locale)}` })}</span>{' '}
              <span className="text-muted-foreground">{t('analytics.insights.times.bestDetail', { median: formatMetric(grid.best.medianViews, locale), count: grid.best.posts })}</span>
            </p>
          )}
          <p className="mt-1 text-[11px] text-muted-foreground">{t('analytics.insights.times.timezone', { timezone: formatTimezoneLabel(timezone) })}</p>
        </InsightCardBody>
      </CardContent>
    </Card>
  );
};
